import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { api, apiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { MarketLogo } from "@/components/ConnectPrimaryCard";
import {
  HELPER_BROWSER,
  HELPER_HOST,
  beginConnect,
  helperPostPack,
  helperDelist,
  helperCheckListed,
  helperSeen,
  helperVersion,
  listingBelongsToChannel,
  listingIdFromUrl,
  listingUrlFitsTitle,
  listingUrlInText,
  listingStatusLabel,
  listingUrlExample,
  ebayTitleWarning,
  keepsOwnStoreListing,
  titleMax,
  HIDDEN_SELLER_CHANNELS,
} from "@/lib/marketplaces";
import { useAuth } from "@/context/AuthContext";
import { FIELD_META, GROUP_ORDER, emptyMarketForm, fieldMeta, formFromPack, mapHelperListing, marketForm } from "@/lib/marketForms";
import MercariCategoryField from "@/components/MercariCategoryField";
import { MERCARI_CATEGORY_PATHS } from "@/lib/mercariCategories";
import { DEPOP_CATEGORY_PATHS } from "@/lib/depopCategories";
import { MERCARI_BRANDS } from "@/lib/mercariBrands";

const COMING_SOON_CHANNELS = new Set([
  "myntra", "ajio", "nykaa", "tatacliq", "olx", "meesho", "amazon", "amazon_in", "google_shopping",
]);

const inp = "w-full px-3 py-2 border border-aura-border outline-none text-sm rounded-lg bg-transparent";
const MERCARI_BRAND_SET = new Set(MERCARI_BRANDS);
const MERCARI_CATEGORY_SET = new Set(MERCARI_CATEGORY_PATHS);
const DEPOP_CATEGORY_SET = new Set(DEPOP_CATEGORY_PATHS);

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
}

function fmtAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  return fmtWhen(iso);
}

function statusTone(status) {
  if (status === "listed") return "bg-emerald-500/15 text-emerald-400";
  if (status === "ready") return "bg-aura-accent/15 text-aura-accent";
  if (status === "failed") return "bg-red-500/15 text-red-300";
  if (status === "sold") return "bg-red-500/15 text-red-400";
  return "bg-white/5 text-aura-muted";
}

export default function ItemForm() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setSearchParams] = useSearchParams();
  const rawChannel = params.get("channel") || "shoptosa";
  const picked = rawChannel === "shopify" ? "walmart" : rawChannel;
  const selected = HIDDEN_SELLER_CHANNELS.has(picked) ? "shoptosa" : picked;
  const fileRef = useRef(null);
  const [form, setForm] = useState(emptyMarketForm());
  const [taxonomyPrompt, setTaxonomyPrompt] = useState("");
  const [brandOpen, setBrandOpen] = useState(false);
  const [listingUrl, setListingUrl] = useState("");
  const [waitingListed, setWaitingListed] = useState(false);
  const [helperStamp, setHelperStamp] = useState("");
  const [delisting, setDelisting] = useState(false);
  const [heldDelisted, setHeldDelisted] = useState(false);
  const [retiredIds, setRetiredIds] = useState([]);
  const retiredRef = useRef(new Set());
  retiredRef.current = new Set(retiredIds);
  const [watching, setWatching] = useState(params.get("wait") === "1");
  const watchUntil = useRef(params.get("wait") === "1" ? Date.now() + 5 * 60 * 1000 : 0);
  const startWatching = useCallback(() => {
    watchUntil.current = Date.now() + 5 * 60 * 1000;
    setWatching(true);
  }, []);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["crosslist-item", id],
    queryFn: async () => (await api.get(`/seller/crosslist/item/${id}`)).data,
  });
  const { data: connections } = useQuery({
    queryKey: ["seller-connections"],
    queryFn: async () => (await api.get("/seller/connections")).data,
  });

  useEffect(() => {
    if (selected !== "flipkart") return undefined;
    const read = () => setHelperStamp(helperVersion() || (helperSeen() ? "on" : ""));
    read();
    const poll = setInterval(read, 1000);
    return () => clearInterval(poll);
  }, [selected]);

  const item = data?.item;
  const channels = data?.channels || [];
  const connById = useMemo(() => {
    const map = {};
    for (const c of connections?.channels || []) map[c.id] = c;
    return map;
  }, [connections]);

  const foundChannel = channels.find((c) => c.id === selected) || { id: selected, name: "Shoptosa Shop" };
  const channelMeta = selected === "walmart" ? { ...foundChannel, name: "Shopify" } : foundChannel;
  const chRow = item?.channels?.[selected] || {};
  const status = selected === "shoptosa"
    ? (item?.is_live ? "listed" : "off")
    : (chRow.status || "off");
  const maxTitle = selected === "shoptosa" ? 200 : titleMax(selected);
  const listedOnce = useRef(false);
  const closetCheckOnce = useRef("");
  const lastHelperError = useRef("");
  const delistTimer = useRef(0);
  const titleRef = useRef("");
  titleRef.current = form.title || data?.packs?.[selected]?.title || data?.item?.title || "";
  const sameRetiredId = (id, ids) => {
    const low = String(id || "").toLowerCase();
    if (low.length < 12) return false;
    return (ids || []).some((item) => {
      const other = String(item || "").toLowerCase();
      return other.length >= 12 && (low === other || low.startsWith(other) || other.startsWith(low));
    });
  };
  const urlMismatch = Boolean(
    listingUrl
    && /^https?:\/\//i.test(listingUrl)
    && (/\/listing\//i.test(listingUrl) || /\/listings\//i.test(listingUrl))
    && form.title
    && !listingUrlFitsTitle(listingUrl, form.title)
  );
  const ownListing = Boolean(listingUrl && listingBelongsToChannel(selected, listingUrl));
  const viewStatus = heldDelisted ? "delisted" : status;
  const shownStatus = selected !== "shoptosa" && status === "listed" && !ownListing && !heldDelisted
    ? "off"
    : viewStatus;
  const looksListed = !heldDelisted && !urlMismatch && status !== "delisted" && status !== "sold" && status !== "failed" && ownListing;
  const hasListingLink = Boolean(listingUrl && /^https?:\/\//i.test(listingUrl));
  const hasUnifiedListing = hasListingLink || Boolean(chRow.remote_id);
  const showUnifiedDelist = hasUnifiedListing && status !== "delisted";
  const showUnifiedPost = status !== "listed";

  const persistListed = useCallback(async (url, { toast: showToast = false, listingId = "", listingTitle = "", trusted = false, source = "helper" } = {}) => {
    const clean = String(url || "").trim().split("?")[0];
    if (!clean || !/^https?:\/\//i.test(clean)) return;
    if (/create-listing|\/sell\/?$/i.test(clean)) return;
    if (!listingBelongsToChannel(selected, clean)) return;
    const incomingId = String(listingIdFromUrl(clean, selected) || "").toLowerCase();
    if (sameRetiredId(incomingId, [...retiredRef.current])) return;
    const fits = !titleRef.current || listingUrlFitsTitle(clean, titleRef.current);
    if (!fits && source !== "paste") return;
    const already = listedOnce.current;
    setListingUrl(clean);
    setWaitingListed(false);
    setWatching(false);
    listedOnce.current = true;
    try {
      await api.put(`/seller/crosslist/${id}`, {
        channels: { [selected]: { listing_url: clean, status: "listed", ...(listingTitle ? { listing_title: listingTitle } : {}) } },
      });
    } catch {
      /* helper may already have saved it */
    }
    qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
    qc.invalidateQueries({ queryKey: ["crosslist-items"] });
    if (showToast && !already) toast.success(listingId ? `Listed · ID ${listingId}` : "Your item is listed");
  }, [id, selected, qc]);

  useEffect(() => {
    if (params.get("channel") !== "walmart") return;
    const next = new URLSearchParams(params);
    next.set("channel", "shopify");
    setSearchParams(next, { replace: true });
  }, [params, setSearchParams]);

  useEffect(() => {
    if (params.get("wait") !== "1") return;
    const next = new URLSearchParams(params);
    next.delete("wait");
    setSearchParams(next, { replace: true });
  }, [params, setSearchParams]);

  useEffect(() => {
    if (!data?.item) return;
    const mapped = mapHelperListing(selected, formFromPack(data.packs?.[selected], data.item));
    setForm(mapped.fields);
    setTaxonomyPrompt(mapped.prompt || "");
    const channelRow = data.item?.channels?.[selected] || {};
    const channelStatus = String(channelRow.status || "");
    const raw = String(channelRow.listing_url || "").trim();
    const url = channelStatus === "delisted" ? "" : (raw && listingBelongsToChannel(selected, raw) ? raw : "");
    const title = data?.packs?.[selected]?.title || data?.item?.title || "";
    const usable = Boolean(url && (keepsOwnStoreListing(selected) || !title || listingUrlFitsTitle(url, title)));
    const rawId = String(listingIdFromUrl(url, selected) || "").toLowerCase();
    const fromServer = ((data.item?.channels?.[selected] || {}).retired_ids || [])
      .map((id) => String(id || "").toLowerCase())
      .filter(Boolean);
    if (fromServer.some((id) => !retiredRef.current.has(id))) {
      setRetiredIds((prev) => [...new Set([...prev, ...fromServer])]);
    }
    const blocked = sameRetiredId(rawId, [...retiredRef.current, ...fromServer]);
    setListingUrl((current) => {
      if (channelStatus === "delisted") return "";
      const next = usable && !blocked ? url : "";
      if (next) return next;
      const currentId = String(listingIdFromUrl(current, selected) || "").toLowerCase();
      const currentRetired = sameRetiredId(currentId, [...retiredRef.current, ...fromServer]);
      if (current && listingBelongsToChannel(selected, current) && !currentRetired) return current;
      return "";
    });
    if (url && !blocked) setWaitingListed(false);
  }, [data, selected]);

  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.data?.type !== "SHOPTOSA_HELPER_LISTED") return;
      if (ev.data.channel && ev.data.channel !== selected) return;
      if (ev.data.product_id && ev.data.product_id !== id) return;
      if (ev.data.listing_url) {
        persistListed(ev.data.listing_url, {
          toast: true,
          listingId: ev.data.listing_id || "",
          listingTitle: ev.data.listing_title || "",
          trusted: true,
        });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [id, selected, persistListed]);

  useEffect(() => {
    const onMsg = (ev) => {
      if (ev.data?.type !== "SHOPTOSA_HELPER_NEEDS_YOU") return;
      if (ev.data.channel && ev.data.channel !== selected) return;
      if (ev.data.product_id && ev.data.product_id !== id) return;
      setWaitingListed(false);
      setWatching(false);
      const text = ev.data.error || "This shop needs a detail before the listing can finish. Stay on this page.";
      if (ev.data.channel === "flipkart" && /quality check|assigns the fsn/i.test(text)) toast.message(text);
      else toast.error(text);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [id, selected]);

  useEffect(() => {
    if (status === "delisted") setHeldDelisted(false);
    if (status === "listed") setDelisting(false);
  }, [status]);

  useEffect(() => {
    const onMsg = (ev) => {
      const type = ev.data?.type;
      if (type !== "SHOPTOSA_HELPER_DELISTED" && type !== "SHOPTOSA_HELPER_DELIST_FAILED") return;
      if (ev.data.channel && ev.data.channel !== selected) return;
      if (ev.data.product_id && ev.data.product_id !== id) return;
      window.clearTimeout(delistTimer.current);
      setDelisting(false);
      if (type === "SHOPTOSA_HELPER_DELIST_FAILED") {
        toast.error(ev.data.error || "Poshmark did not delete the listing.");
        return;
      }
      setHeldDelisted(true);
      const gone = String(listingIdFromUrl(listingUrl, selected) || "").toLowerCase();
      if (gone) setRetiredIds((prev) => [...new Set([...prev, gone])]);
      setListingUrl("");
      qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      qc.invalidateQueries({ queryKey: ["crosslist-items"] });
      toast.success("Delisted");
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [id, selected, qc]);

  useEffect(() => {
    if (!watching || selected === "shoptosa") return;
    let stop = false;
    let inFlight = false;
    const run = async () => {
      if (stop || inFlight) return;
      inFlight = true;
      try {
        const res = await helperCheckListed(selected, {
          productId: id,
          title: titleRef.current,
          handle: connById[selected]?.handle || "",
        });
        if (stop) return;
        if (res.error === "helper-missing" || res.error === "timeout") {
          return;
        }
        if (res.error && res.error !== lastHelperError.current) {
          lastHelperError.current = res.error;
          toast.error(res.error);
        }
        if (!res.error) lastHelperError.current = "";
        if (res.listed && res.listing_url) await persistListed(res.listing_url, { toast: true, trusted: true });
      } finally {
        inFlight = false;
      }
    };
    run();
    const poll = setInterval(run, 8000);
    const left = Math.max(1000, watchUntil.current - Date.now());
    const stopWatch = setTimeout(() => {
      if (stop) return;
      setWatching(false);
      setWaitingListed(false);
    }, left);
    return () => {
      stop = true;
      clearInterval(poll);
      clearTimeout(stopWatch);
    };
  }, [watching, selected, id, persistListed, connById]);

  useEffect(() => {
    if ((!watching && !waitingListed) || selected === "shoptosa") return;
    const grab = async () => {
      try {
        const text = await navigator.clipboard.readText();
        const url = listingUrlInText(selected, text);
        if (url) await persistListed(url, { toast: true, trusted: true });
      } catch {
        /* clipboard permission is optional */
      }
    };
    const onVis = () => { if (!document.hidden) grab(); };
    window.addEventListener("focus", grab);
    document.addEventListener("visibilitychange", onVis);
    const poll = setInterval(grab, 4000);
    grab();
    return () => {
      window.removeEventListener("focus", grab);
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(poll);
    };
  }, [watching, waitingListed, selected, persistListed]);

  useEffect(() => {
    if ((!watching && !waitingListed) || selected === "shoptosa") return;
    let stop = false;
    const run = async () => {
      try {
        const { data: latest } = await api.get(`/seller/crosslist/item/${id}`);
        if (stop) return;
        const row = latest?.item?.channels?.[selected] || {};
        const url = row.listing_url || "";
        if (url && (/\/listing\//i.test(url) || listingUrlInText(selected, url))) {
          await persistListed(url, { toast: true, trusted: true, listingId: row.remote_id || "" });
        }
      } catch {
        /* stay on this screen; helper POST may land a moment later */
      }
    };
    run();
    const poll = setInterval(run, 2500);
    return () => {
      stop = true;
      clearInterval(poll);
    };
  }, [watching, waitingListed, selected, id, persistListed]);

  useEffect(() => {
    if (!HELPER_BROWSER.has(selected) || selected === "shoptosa" || !item?.id) return;
    if (watching) return;
    if (listingUrl && /^https?:\/\//i.test(listingUrl) && !/create-listing/i.test(listingUrl)) return;
    if (status === "sold" || status === "delisted") return;
    const key = `${id}:${selected}`;
    if (closetCheckOnce.current === key) return;
    closetCheckOnce.current = key;
    let stop = false;
    (async () => {
      const res = await helperCheckListed(selected, {
        productId: id,
        title: titleRef.current,
        handle: connById[selected]?.handle || "",
      });
      if (res.error === "helper-missing") return;
      if (res.error && res.error !== lastHelperError.current) {
        lastHelperError.current = res.error;
        toast.error(res.error);
      }
      if (!res.error) lastHelperError.current = "";
      if (!stop && res.listed && res.listing_url) await persistListed(res.listing_url, { toast: true, trusted: true });
    })();
    return () => { stop = true; };
  }, [selected, id, item?.id, listingUrl, status, persistListed, connById, watching]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async ({ silent = false } = {}) => {
    if (!item) return;
    if (selected === "mercari") {
      const price = Number(form.price);
      if (!(price >= 1 && price <= 2000)) {
        toast.error("Mercari item price must be from $1.00 to $2,000.00.");
        return;
      }
      if (!String(form.condition || "").trim()) {
        toast.error("Condition is required.");
        return;
      }
      if (!String(form.category || "").trim()) {
        toast.error("Category is required.");
        return;
      }
      if (!form.free_shipping && (form.shipping_cost === "" || form.shipping_cost == null)) {
        toast.error("Shipping cost is required.");
        return;
      }
      if (form.category && !MERCARI_CATEGORY_SET.has(form.category)) {
        toast.error("Category must be one of Mercari's categories.");
        return;
      }
      const brandName = String(form.brand || "").trim();
      if (brandName && brandName !== "No Brand/Not Sure" && !MERCARI_BRAND_SET.has(brandName)) {
        toast.error("Brand must be a name from Mercari's list, or No Brand/Not Sure.");
        return;
      }
    }
    if (selected === "depop") {
      if (!String(form.condition || "").trim()) {
        toast.error("Condition is required.");
        return;
      }
      if (!String(form.category || "").trim()) {
        toast.error("Category is required.");
        return;
      }
      if (form.category && !DEPOP_CATEGORY_SET.has(form.category)) {
        toast.error("Category must be one of Depop's categories.");
        return;
      }
    }
    if (!silent) setBusy(true);
    try {
      const body = {
        title: form.title,
        description: form.description,
        price: Number(form.price) || 0,
        quantity: selected === "mercari" ? 1 : (Number(form.quantity) || 0),
        brand: form.brand,
        size: form.size,
        color: form.color,
        secondary_color: form.secondary_color,
        sku: form.sku,
        images: form.images,
        category: form.category,
        condition: form.condition,
        tags: form.tags,
        original_price: form.original_price === "" ? null : Number(form.original_price),
        minimum_price: form.minimum_price === "" ? null : Number(form.minimum_price),
        cost_price: form.cost_price === "" ? null : Number(form.cost_price),
        shipping_cost: form.free_shipping ? 0 : (form.shipping_cost === "" ? null : Number(form.shipping_cost)),
        free_shipping: !!form.free_shipping,
        location: form.location || "",
        delivery_method: form.delivery_method || "",
        hide_from_friends: !!form.hide_from_friends,
        manufacturer: form.manufacturer || "",
        country_of_origin: form.country_of_origin || "",
        package_weight: form.package_weight === "" || form.package_weight == null ? "" : Number(form.package_weight),
        package_weight_unit: form.package_weight_unit || "",
        battery_quantity: form.battery_quantity === "" || form.battery_quantity == null ? "" : Number(form.battery_quantity),
        hsn: form.hsn || "",
      };
      let dataRes = null;
      if (selected === "shoptosa") {
        dataRes = (await api.put(`/seller/crosslist/${id}`, body)).data;
      } else {
        const channelBody = { ...body };
        if (String(listingUrl || "").trim()) channelBody.listing_url = String(listingUrl).trim();
        if (HELPER_BROWSER.has(selected) && (/\/listing\//i.test(listingUrl) || listingUrlInText(selected, listingUrl)) && status !== "sold" && status !== "delisted") {
          channelBody.status = "listed";
        }
        dataRes = (await api.put(`/seller/crosslist/${id}`, { channels: { [selected]: channelBody } })).data;
      }
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      await qc.invalidateQueries({ queryKey: ["crosslist-items"] });
      const synced = dataRes?.channel_sync?.[selected];
      if (!silent) {
        if (synced?.status === "failed") toast.error(synced.note || `${channelMeta.name} update failed`);
        else if (synced?.status === "listed") toast.success(`Saved and updated on ${channelMeta.name}`);
        else if (synced?.warning) toast.message(synced.warning);
        else toast.success(`${channelMeta.name} form saved`);
      }
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      if (!silent) setBusy(false);
    }
  };

  const resetForm = async () => {
    if (selected === "shoptosa") {
      setForm(formFromPack(data?.packs?.shoptosa, item));
      return;
    }
    setBusy(true);
    try {
      await api.put(`/seller/crosslist/${id}`, { channels: { [selected]: { reset_form: true } } });
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      toast.success("Reset to Shoptosa form");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const markListed = async (next) => {
    setBusy(true);
    try {
      if (selected === "shoptosa") {
        await api.post("/seller/crosslist/bulk", {
          product_ids: [id],
          action: next === "listed" ? "live" : "delist",
        });
      } else {
        await api.put(`/seller/crosslist/${id}`, { channels: { [selected]: { status: next, listing_url: listingUrl } } });
      }
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      toast.success(next === "listed" ? "Marked listed" : "Marked not listed");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyPack = async () => {
    const bits = [form.title, form.description, form.price ? `Price: ${form.price}` : ""].filter(Boolean);
    await navigator.clipboard.writeText(bits.join("\n\n"));
    toast.success("Copied listing pack");
  };

  const clearListingUrl = async () => {
    setListingUrl("");
    setWatching(false);
    listedOnce.current = false;
    setBusy(true);
    try {
      await api.put(`/seller/crosslist/${id}`, { channels: { [selected]: { listing_url: "", status: "off" } } });
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      toast.success("Cleared the listing URL for this item");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openListingTab = (url) => {
    const open = window.__shoptosaNativeOpen || window.open.bind(window);
    open(url, "_blank", "noopener,noreferrer");
  };

  const viewListing = () => {
    if (!listingUrl) {
      toast.error(`Paste a ${channelMeta.name} listing URL first. It looks like ${listingUrlExample(selected)}`);
      return;
    }
    openListingTab(listingUrl);
  };

  const helperPost = async () => {
    if (looksListed) {
      toast.success("Your item is listed");
      return;
    }
    listedOnce.current = false;
    if (!helperSeen()) {
      toast.error("Load the Shoptosa helper (puzzle icon → allow shoptosa.com), hard refresh (Ctrl+Shift+R), then click List again.");
      return;
    }
    setWaitingListed(true);
    try {
      await save();
      const pack = {
        product_id: item.id,
        id: item.id,
        title: form.title,
        description: form.description,
        price: form.price,
        original_price: form.original_price,
        minimum_price: form.minimum_price,
        cost_price: form.cost_price,
        quantity: selected === "mercari" ? 1 : form.quantity,
        brand: form.brand,
        color: form.color,
        secondary_color: form.secondary_color,
        sku: form.sku,
        category: form.category,
        size: form.size || data?.packs?.shoptosa?.size || item?.size || "",
        condition: [form.condition, data?.packs?.shoptosa?.condition, item?.condition].map((value) => String(value || "").trim()).find((value) => value && !/^select\b/i.test(value)) || "",
        tags: form.tags,
        shipping_cost: form.shipping_cost === "" || form.shipping_cost == null
          ? (Number(item?.shipping_domestic) > 0 ? item.shipping_domestic : "")
          : form.shipping_cost,
        location: form.location || [user?.profile?.address_city, user?.profile?.address_region].filter(Boolean).join(", "),
        delivery_method: form.delivery_method || "Shipping",
        hide_from_friends: !!form.hide_from_friends,
        ship_from: {
          name: user?.profile?.address_name || "",
          line1: user?.profile?.address_line1 || "",
          line2: user?.profile?.address_line2 || "",
          city: user?.profile?.address_city || "",
          region: user?.profile?.address_region || "",
          postal: user?.profile?.address_postal || "",
          country: user?.profile?.address_country || user?.profile?.country || "",
          phone: user?.profile?.address_phone || "",
        },
        images: form.images,
        listing_url: (viewStatus === "delisted" || status === "delisted" || sameRetiredId(listingIdFromUrl(listingUrl, selected), retiredIds))
          ? ""
          : (urlMismatch ? "" : listingUrl),
        exclude_ids: retiredIds,
        handle: connById[selected]?.handle || "",
      };
      const res = await helperPostPack(selected, pack);
      if (res.listed && res.listing_url) {
        await persistListed(res.listing_url, { toast: true, trusted: true });
        return;
      }
      if (res.error === "helper-missing") {
        setWatching(false);
        toast.error("Helper did not respond. Reload it (puzzle icon → reload), hard refresh this page, then click List again.");
        return;
      }
      startWatching();
    } catch (e) {
      setWatching(false);
      toast.error(apiError(e) || "Could not reach the helper. Reload it and try again.");
    } finally {
      setWaitingListed(false);
    }
  };

  const setChannelStatus = async (next, message) => {
    setBusy(true);
    try {
      await api.put(`/seller/crosslist/${id}`, { channels: { [selected]: { status: next, listing_url: listingUrl } } });
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      toast.success(message);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const delistOnMarket = async () => {
    if (!listingUrl) {
      toast.error(`A listing URL is required to delist. Paste one like ${listingUrlExample(selected)}`);
      return;
    }
    if (HELPER_BROWSER.has(selected)) {
      if (!helperSeen()) {
        toast.error("The Shoptosa helper is not on this tab. Reload it, hard-refresh this page, then click Delist again.");
        return;
      }
      setDelisting(true);
      window.clearTimeout(delistTimer.current);
      delistTimer.current = window.setTimeout(() => {
        setDelisting(false);
        toast.error(`Delisting is still running. Stay on this page — it will say Delisted when ${channelMeta.name} finishes. If it does not, click Delist again.`);
      }, 90000);
      toast.message("Stay on this page. Delisting runs here, with no other tab.");
      try {
        const res = await helperDelist(selected, {
          productId: id,
          url: listingUrl,
          handle: connById[selected]?.handle || "",
        });
        if (res?.error) {
          window.clearTimeout(delistTimer.current);
          setDelisting(false);
          toast.error(res.error === "helper-missing"
            ? "The Shoptosa helper is not on this tab. Reload it, hard-refresh this page, then click Delist again."
            : res.error);
        }
      } catch (e) {
        window.clearTimeout(delistTimer.current);
        setDelisting(false);
        toast.error(apiError(e) || "Could not start delisting.");
      }
      return;
    }
    openListingTab(listingUrl);
    await setChannelStatus("delisted", `Opened ${channelMeta.name}. Delist it there — Shoptosa cannot click Delist for you.`);
  };

  const markSold = async () => {
    await setChannelStatus("sold", "Marked as sold");
  };

  const unifiedPost = async () => {
    setBusy(true);
    try {
      const saved = await save({ silent: true });
      if (!saved) return;
      const res = (await api.post(`/seller/connections/${selected}/unified/publish`, { product_ids: [id] })).data;
      const url = res.listings?.[0]?.listing_url || "";
      const note = String(res.listings?.[0]?.note || "");
      const postedStatus = res.listings?.[0]?.status || "";
      if (url) setListingUrl(url.split("?")[0]);
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      await qc.invalidateQueries({ queryKey: ["seller-connection", selected] });
      if (postedStatus === "failed") toast.error(note || `${channelMeta.name} did not accept this listing`);
      else toast.success(url ? (status === "listed" ? "Updated" : "Posted") : (res.posted ? `Posted ${res.posted}` : "Nothing posted"));
      if (url && postedStatus !== "failed") toast.message(url);
      if (postedStatus !== "listed" && res.listings?.[0]?.warning) toast.message(res.listings[0].warning);
      if (/stock|public page/i.test(note) && postedStatus !== "failed") toast.message(note);
      if (res.errors?.length) toast.error(res.errors[0].error);
    } catch (e) {
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      const fresh = qc.getQueryData(["crosslist-item", id]);
      const saved = String(fresh?.item?.channels?.[selected]?.listing_url || "").split("?")[0].trim();
      if (saved && listingBelongsToChannel(selected, saved)) {
        setListingUrl(saved);
        toast.success("Posted");
        toast.message(saved);
      } else {
        const message = apiError(e);
        if (selected === "flipkart" && /seller hub/i.test(message)) {
          toast.message("Flipkart has no page for this SKU yet. The helper is opening Seller Hub.");
          setBusy(false);
          await helperPost();
          return;
        }
        toast.error(message);
      }
    } finally {
      setBusy(false);
    }
  };

  const unifiedDelist = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/connections/${selected}/unified/delist`, { product_ids: [id] });
      setListingUrl("");
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      await qc.invalidateQueries({ queryKey: ["seller-connection", selected] });
      toast.success(`Delisted on ${channelMeta.name}`);
    } catch (e) {
      toast.error(apiError(e));
      await qc.invalidateQueries({ queryKey: ["crosslist-item", id] });
      await qc.invalidateQueries({ queryKey: ["seller-connection", selected] });
    } finally {
      setBusy(false);
    }
  };

  const addPhotos = async (list) => {
    const spec = marketForm(selected);
    const photoMax = spec.photoMax || 12;
    const files = Array.from(list || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const accepted = [];
    for (const file of files) {
      if (spec.photoBytes && file.size > spec.photoBytes) {
        toast.error(`${file.name} is over 15mb. ${channelMeta.name}'s limit is 15mb per photo.`);
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of accepted) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(up.url || up.path);
      }
      setForm((f) => {
        const images = [...f.images, ...urls].slice(0, photoMax);
        if (f.images.length + urls.length > photoMax) {
          toast.error(`${channelMeta.name} keeps the first ${photoMax} photos.`);
        }
        return { ...f, images };
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const selectChannel = (cid) => {
    const next = new URLSearchParams(params);
    const shown = cid === "walmart" ? "shopify" : cid;
    if (shown === "shoptosa") next.delete("channel");
    else next.set("channel", shown);
    setSearchParams(next, { replace: true });
  };

  if (isLoading) {
    return <div className="max-w-[1200px] mx-auto px-6 pt-28 text-aura-muted">Loading item form…</div>;
  }
  if (error || !item) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 pt-28">
        <p className="text-aura-muted">{error ? apiError(error) : "Item not found"}</p>
        <Link to="/seller/crosslist" className="text-aura-cyan text-sm mt-3 inline-block">Back to Inventory</Link>
      </div>
    );
  }

  const connected = selected === "shoptosa" || connById[selected]?.connected || connById[selected]?.native;
  const via = connById[selected]?.connect_via;
  const marketplaceChannels = channels.filter((c) => c.id !== "shoptosa" && !COMING_SOON_CHANNELS.has(c.id) && !HIDDEN_SELLER_CHANNELS.has(c.id));
  const comingSoonChannels = channels.filter((c) => COMING_SOON_CHANNELS.has(c.id) && !HIDDEN_SELLER_CHANNELS.has(c.id));
  const template = marketForm(selected);
  const detailFields = template.fields.filter((k) => k !== "images");
  const groupedFields = {};
  detailFields.forEach((k) => {
    const meta = fieldMeta(selected, k);
    if (!meta) return;
    const g = meta.group || "Item Details";
    (groupedFields[g] = groupedFields[g] || []).push(k);
  });
  const extraGroups = Object.keys(groupedFields).filter((g) => !GROUP_ORDER.includes(g));
  const orderedGroups = [...GROUP_ORDER, ...extraGroups].filter((g) => groupedFields[g]?.length);

  const renderField = (key) => {
    const meta = fieldMeta(selected, key);
    if (!meta) return null;
    const isTitle = key === "title";
    const max = isTitle ? maxTitle : meta.max;
    const isNum = meta.kind === "number";
    const required = meta.required && !(key === "shipping_cost" && form.free_shipping);
    const labelNode = (
      <>
        {meta.label}
        {required ? <span className="text-red-400"> *</span> : null}
      </>
    );
    if (meta.kind === "readonly") {
      return (
        <label key={key} className="block text-sm">
          {labelNode}
          <input value={meta.value || ""} readOnly disabled className={`${inp} mt-1 opacity-70`} />
          {meta.hint ? <div className="text-[11px] text-aura-muted mt-1">{meta.hint}</div> : null}
        </label>
      );
    }
    if (meta.kind === "checkbox") {
      return (
        <label key={key} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form[key]}
            onChange={(e) => {
              const on = e.target.checked;
              if (key === "free_shipping") {
                setForm((f) => ({
                  ...f,
                  free_shipping: on,
                  shipping_cost: on ? 0 : (f.shipping_cost === 0 || f.shipping_cost === "0" ? "" : f.shipping_cost),
                }));
                return;
              }
              setField(key, on);
            }}
          />
          {meta.label}
        </label>
      );
    }
    if (meta.kind === "mercari-brand") {
      const noBrand = (form.brand || "") === "No Brand/Not Sure";
      const query = noBrand ? "" : String(form.brand || "").trim().toLowerCase();
      const exact = !noBrand && MERCARI_BRAND_SET.has(form.brand || "");
      const matches = [];
      if (query && !exact) {
        const starts = [];
        const rest = [];
        for (const name of MERCARI_BRANDS) {
          const lower = name.toLowerCase();
          if (lower.startsWith(query)) starts.push(name);
          else if (lower.includes(query)) rest.push(name);
          if (starts.length >= 100) break;
        }
        matches.push(...starts.concat(rest).slice(0, 100));
      }
      return (
        <div key={key} className="block text-sm">
          <label className="block">
            {labelNode}
            <input
              value={noBrand ? "" : (form.brand || "")}
              disabled={noBrand}
              placeholder="Select from the list"
              onFocus={() => setBrandOpen(true)}
              onBlur={() => window.setTimeout(() => setBrandOpen(false), 180)}
              onChange={(e) => {
                setBrandOpen(true);
                setField("brand", e.target.value);
              }}
              className={`${inp} mt-1 disabled:opacity-50`}
            />
          </label>
          {brandOpen && matches.length ? (
            <ul className="mt-1 max-h-48 overflow-auto rounded-lg border border-aura-border bg-aura-surface">
              {matches.map((name) => (
                <li key={name}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setField("brand", name);
                      setBrandOpen(false);
                    }}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-aura-accent/10"
                  >
                    {name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {query && !exact && !matches.length ? (
            <div className="text-[11px] text-aura-muted mt-1">That name is not on Mercari's brand list.</div>
          ) : null}
          {!query && !noBrand && brandOpen ? (
            <div className="mt-1 rounded-lg border border-aura-border bg-aura-surface px-3 py-2 text-sm text-aura-muted">
              There are no brands
            </div>
          ) : null}
          <label className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              checked={noBrand}
              onChange={(e) => setField("brand", e.target.checked ? "No Brand/Not Sure" : "")}
            />
            No Brand/Not Sure
          </label>
          {meta.hint ? <div className="text-[11px] text-aura-muted mt-1">{meta.hint}</div> : null}
        </div>
      );
    }
    if (meta.kind === "mercari-category" || meta.kind === "depop-category") {
      return (
        <div key={key}>
          <div className="text-sm">{labelNode}</div>
          <MercariCategoryField
            value={form.category || ""}
            onChange={(path) => setField("category", path)}
            className={inp}
            paths={meta.kind === "depop-category" ? DEPOP_CATEGORY_PATHS : undefined}
          />
          {meta.hint ? <div className="text-[11px] text-aura-muted mt-1">{meta.hint}</div> : null}
        </div>
      );
    }
    if (meta.kind === "depop-brand") {
      const noBrand = (form.brand || "") === "No Brand";
      return (
        <div key={key} className="block text-sm">
          <label className="block">
            {labelNode}
            <input
              value={noBrand ? "" : (form.brand || "")}
              disabled={noBrand}
              placeholder="Type to search brands"
              onChange={(e) => setField("brand", e.target.value)}
              className={`${inp} mt-1 disabled:opacity-50`}
            />
          </label>
          <label className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              checked={noBrand}
              onChange={(e) => setField("brand", e.target.checked ? "No Brand" : "")}
            />
            No Brand
          </label>
          {meta.hint ? <div className="text-[11px] text-aura-muted mt-1">{meta.hint}</div> : null}
        </div>
      );
    }
    if (meta.kind === "textarea") {
      return (
        <label key={key} className="block text-sm">
          {labelNode}
          <textarea
            value={form[key] || ""}
            maxLength={max || undefined}
            onChange={(e) => setField(key, e.target.value)}
            rows={6}
            className={`${inp} mt-1`}
          />
          {meta.counter ? (
            <div className="text-[11px] text-aura-muted mt-1">
              {(form[key] || "").length}{max ? ` / ${max}` : ""} characters
            </div>
          ) : null}
        </label>
      );
    }
    if (meta.kind === "select" && (meta.options || []).length > 40) {
      const listId = `${selected}-${key}-options`;
      const options = (meta.options || []).filter(Boolean);
      return (
        <label key={key} className="block text-sm">
          {labelNode}
          <input
            list={listId}
            value={form[key] || ""}
            onChange={(e) => setField(key, e.target.value)}
            placeholder="Type to search categories"
            className={`${inp} mt-1`}
          />
          <datalist id={listId}>
            {options.map((o) => <option key={o} value={o} />)}
          </datalist>
          {meta.hint ? <div className="text-[11px] text-aura-muted mt-1">{meta.hint}</div> : null}
        </label>
      );
    }
    if (meta.kind === "select") {
      return (
        <label key={key} className="block text-sm">
          {labelNode}
          <select value={form[key] || ""} onChange={(e) => setField(key, e.target.value)} className={`${inp} mt-1`}>
            {(() => {
              const options = [...(meta.options || [])];
              const current = form[key] || "";
              if (current && !options.includes(current)) options.splice(1, 0, current);
              return options.map((o) => (
                <option key={o || "empty"} value={o}>{o || "Select…"}</option>
              ));
            })()}
          </select>
          {meta.hint ? <div className="text-[11px] text-aura-muted mt-1">{meta.hint}</div> : null}
        </label>
      );
    }
    const moneyText = (value) => {
      if (value === "" || value == null) return "$--";
      const n = Number(value);
      if (!Number.isFinite(n)) return "$--";
      return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
    };
    return (
      <label key={key} className="block text-sm">
        {labelNode}
        <div className="relative mt-1">
          {meta.money ? <span className="absolute left-3 top-2 text-sm text-aura-muted">$</span> : null}
          <input
            type={isNum ? "number" : "text"}
            min={isNum ? String(meta.min != null ? meta.min : 0) : undefined}
            max={isNum && meta.max != null ? String(meta.max) : undefined}
            step={key.includes("price") || key === "shipping_cost" ? "0.01" : undefined}
            maxLength={!isNum ? (max || undefined) : undefined}
            disabled={key === "shipping_cost" && !!form.free_shipping}
            value={form[key] ?? ""}
            onChange={(e) => {
              let next = e.target.value;
              if (isNum && meta.max != null && next !== "" && Number(next) > Number(meta.max)) next = String(meta.max);
              setField(key, next);
            }}
            className={`${inp} ${meta.money ? "pl-7" : ""} ${isNum || key === "sku" ? "font-mono" : ""} disabled:opacity-50`}
          />
        </div>
        {isTitle && max ? (
          <div className="text-[11px] text-aura-muted mt-1">{(form.title || "").length} / {max} characters</div>
        ) : null}
        {meta.hint ? <div className="text-[11px] text-aura-muted mt-1">{meta.hint}</div> : null}
        {meta.priceSummary ? (
          <div className="mt-4 text-sm">
            <div className="font-medium mb-2">Price summary</div>
            <div className="flex justify-between py-1"><span>Item price</span><span>{moneyText(form.price === "" ? 0 : form.price)}</span></div>
            <div className="flex justify-between py-1"><span>Shipping cost</span><span>{form.free_shipping || form.shipping_cost === 0 || form.shipping_cost === "0" ? moneyText(0) : moneyText(form.shipping_cost)}</span></div>
            <div className="flex justify-between py-1 font-medium">
              <span>Listing price</span>
              <span>{moneyText((Number(form.price) || 0) + (form.shipping_cost === "" || form.shipping_cost == null ? 0 : (Number(form.shipping_cost) || 0)))}</span>
            </div>
          </div>
        ) : null}
      </label>
    );
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 pt-24 pb-24" data-testid="item-form">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-semibold">Item Form</h1>
        <Link to="/seller/add" className="px-4 py-2.5 bg-aura-accent text-white text-sm rounded-full inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Create Item
        </Link>
      </div>

      <div className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-start">
        <aside className="space-y-3">
          <Link to="/seller/crosslist" className="block w-full text-center px-4 py-2 border border-aura-border rounded-full text-sm">
            ← Back to Inventory
          </Link>
          <div className="bg-aura-surface border border-aura-border rounded-2xl overflow-hidden">
            <div className="px-4 py-3 text-[11px] tracking-luxe uppercase text-aura-muted">Step 1 Shoptosa Form</div>
            <button
              type="button"
              onClick={() => selectChannel("shoptosa")}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left border-t border-aura-border ${selected === "shoptosa" ? "bg-aura-accent/15" : ""}`}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-xs font-semibold text-black">S</span>
              <span className="flex-1 font-medium text-sm">Shoptosa</span>
              <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${statusTone(item.is_live ? "listed" : "off")}`}>
                {item.is_live ? "In progress" : "Draft"}
              </span>
            </button>
          </div>
          <div className="bg-aura-surface border border-aura-border rounded-2xl overflow-hidden">
            <div className="px-4 py-3 text-[11px] tracking-luxe uppercase text-aura-muted">Step 2 Marketplace Forms</div>
            {marketplaceChannels.map((c) => {
              const st = item.channels?.[c.id]?.status || "off";
              const on = selected === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => selectChannel(c.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-t border-aura-border ${on ? "bg-aura-accent/15" : ""}`}
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white shrink-0">
                    <MarketLogo id={c.id} />
                  </span>
                  <span className="flex-1 text-sm truncate">{c.name}</span>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 ${statusTone(st)}`}>
                    {listingStatusLabel(st)}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="bg-aura-surface border border-aura-border rounded-2xl overflow-hidden">
            <div className="px-4 py-3 text-[11px] tracking-luxe uppercase text-aura-muted">Connect more marketplaces coming soon</div>
            {comingSoonChannels.map((c) => (
              <div key={c.id} className="w-full flex items-center gap-3 px-4 py-2.5 text-left border-t border-aura-border opacity-80">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white shrink-0">
                  <MarketLogo id={c.id} />
                </span>
                <span className="flex-1 text-sm truncate">{c.name}</span>
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 bg-white/5 text-aura-muted">
                  Coming soon
                </span>
              </div>
            ))}
          </div>
          <Link
            to="/seller/connections"
            className="block w-full text-center px-4 py-2.5 border border-dashed border-aura-accent text-aura-accent text-sm rounded-full"
          >
            Connect More Marketplaces
          </Link>
        </aside>

        <section className="bg-aura-surface border border-aura-border rounded-2xl p-6 lg:p-8 min-h-[70vh]">
          {COMING_SOON_CHANNELS.has(selected) ? (
            <div className="py-16 text-center">
              <h2 className="text-2xl font-semibold">{channelMeta.name}</h2>
              <p className="text-sm text-aura-muted mt-3">Coming soon. Sellers cannot connect this shop yet.</p>
            </div>
          ) : (
          <>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
            <h2 className="text-2xl font-semibold text-aura-accent">{channelMeta.name} Form</h2>
            <div className="text-right text-xs text-aura-muted">
              <div>Shoptosa Item ID</div>
              <div className="font-mono text-aura-ink mt-0.5">{item.id}</div>
              <div className="mt-2 flex justify-end">
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${statusTone(shownStatus)}`}>
                  {selected === "shoptosa" ? (item.is_live ? "Complete" : "Draft") : listingStatusLabel(shownStatus)}
                </span>
              </div>
            </div>
          </div>

          {shownStatus === "failed" && chRow.note ? (
            <div className="mb-6 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="channel-error">
              {chRow.note}
            </div>
          ) : null}
          {selected === "ebay" && ebayTitleWarning(form.title) ? (
            <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="ebay-title-warning">
              {ebayTitleWarning(form.title)}
            </div>
          ) : null}
          {taxonomyPrompt ? (
            <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200" data-testid="taxonomy-prompt">
              {taxonomyPrompt}
            </div>
          ) : null}

          {selected !== "shoptosa" && HELPER_BROWSER.has(selected) ? (
            <div className="mb-8 rounded-2xl border border-aura-border bg-aura-surface p-5" data-testid="listed-panel">
              <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">{channelMeta.name} connection details</div>
              <div className="flex flex-wrap items-start justify-between gap-4 pb-5 border-b border-aura-border">
                <div className="grid sm:grid-cols-3 gap-6 text-sm flex-1 min-w-[240px]">
                  <div>
                    <div className="text-[11px] uppercase text-aura-muted">{channelMeta.name} Account</div>
                    <div className="mt-1 font-medium">{connById[selected]?.handle || "Signed in on their site"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-aura-muted">Status</div>
                    <div className="mt-1 flex items-center gap-1.5 font-medium">
                      <span className={`w-2 h-2 rounded-full ${connById[selected]?.connected ? "bg-emerald-400" : "bg-aura-muted"}`} />
                      {connById[selected]?.connected ? "Active" : "Not connected"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-aura-muted">Extension Version</div>
                    <div className="mt-1 font-medium">{helperVersion() || "Not on this tab"}</div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Link to={`/seller/connections/${selected}`} className="px-5 py-1.5 text-sm border border-aura-accent text-aura-accent rounded-full text-center">Reconnect</Link>
                  <Link to={`/seller/connections/${selected}`} className="px-5 py-1.5 text-sm border border-aura-border rounded-full text-center">Settings</Link>
                </div>
              </div>
              {!helperVersion() ? (
                <p className="text-sm text-amber-400 mt-4">
                  The Shoptosa helper is not on this tab, so {channelMeta.name} cannot list or delist from here. Remove any old Shoptosa listing helper, re-download the helper here, Load unpacked, Reload the helper → Ctrl+Shift+R this page. Popup must show 1.6.114. Stay on this page. Delist does not open a tab. After you list again, the new listing URL shows up here.
                </p>
              ) : null}
              <div className="text-center pt-6 pb-4">
                <div className="text-2xl font-semibold">
                  {viewStatus === "sold"
                    ? "This item is marked sold"
                    : viewStatus === "delisted"
                      ? "Delisted"
                      : viewStatus === "failed"
                        ? `${channelMeta.name} did not delist this listing`
                      : delisting
                        ? `Delisting on ${channelMeta.name}…`
                      : looksListed
                        ? "🎉 Your Item is Listed"
                        : urlMismatch
                        ? `This ${channelMeta.name} URL is a different item`
                          : waitingListed
                          ? `Listing on ${channelMeta.name}…`
                          : `List this item on ${channelMeta.name}`}
                </div>
                {looksListed && listingUrl ? (
                  <div className="mt-2 text-sm text-aura-cyan break-all select-all">{listingUrl}</div>
                ) : null}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <button type="button" disabled={busy} onClick={save} className="px-5 py-1.5 text-sm border border-aura-accent text-aura-accent rounded-full">Save</button>
                {looksListed ? (
                  <button type="button" onClick={viewListing} className="px-5 py-1.5 text-sm border border-aura-cyan text-aura-cyan rounded-full">View Listing</button>
                ) : (
                  <button type="button" disabled={busy} onClick={helperPost} className="px-5 py-1.5 text-sm bg-aura-accent text-white rounded-full">
                    {waitingListed ? "Listing…" : `List on ${channelMeta.name}`}
                  </button>
                )}
                {viewStatus !== "delisted" ? (
                  <button type="button" disabled={busy || delisting} onClick={delistOnMarket} className="px-5 py-1.5 text-sm border border-aura-border rounded-full text-aura-muted">
                    {delisting ? "Delisting…" : `Delist on ${channelMeta.name}`}
                  </button>
                ) : null}
                <button type="button" disabled={busy} onClick={markSold} className="px-5 py-1.5 text-sm bg-amber-400 text-black rounded-full">
                  Mark as Sold
                </button>
              </div>
              <dl className="grid sm:grid-cols-5 gap-4 text-sm mt-6">
                <div>
                  <dt className="text-[11px] uppercase text-aura-muted">Listing Status</dt>
                  <dd className="mt-1">
                    <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded ${statusTone(urlMismatch ? "ready" : shownStatus)}`}>
                      {urlMismatch ? "Wrong listing" : listingStatusLabel(shownStatus)}
                    </span>
                    {watching && status !== "listed" && !urlMismatch ? (
                      <div className="text-[11px] text-aura-muted mt-1">
                        {selected === "mercari"
                          ? "The helper dismisses Mercari’s template and cookie popups, then clicks List. If one stays open, close it and click List on Mercari."
                          : `Waiting for List on ${channelMeta.name}`}
                      </div>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase text-aura-muted">Created</dt>
                  <dd className="mt-1">{fmtWhen(item.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase text-aura-muted">Last Saved</dt>
                  <dd className="mt-1">{fmtAgo(item.updated_at || chRow.listed_at || item.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase text-aura-muted">Listed Dates</dt>
                  <dd className="mt-1">{fmtWhen(chRow.listed_at)}</dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase text-aura-muted">Listing URL</dt>
                  <dd className="mt-1">
                    {listingUrl && /^https?:\/\//i.test(listingUrl) ? (
                      <span className="text-aura-cyan break-all select-all">{listingUrl}</span>
                    ) : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase text-aura-muted">Live title</dt>
                  <dd className="mt-1 truncate" title={chRow.listing_title || ""}>
                    {chRow.listing_title || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-[11px] uppercase text-aura-muted">Listing ID</dt>
                  <dd className="mt-1 flex items-center gap-2">
                    <span className="font-mono">{chRow?.remote_id || listingIdFromUrl(listingUrl, selected) || "—"}</span>
                    {chRow?.remote_id || listingIdFromUrl(listingUrl, selected) ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const value = chRow?.remote_id || listingIdFromUrl(listingUrl, selected);
                          try {
                            await navigator.clipboard.writeText(value);
                            toast.success("Copied listing ID");
                          } catch {
                            toast.error("Could not copy listing ID");
                          }
                        }}
                        className="text-xs text-aura-cyan"
                      >
                        Copy
                      </button>
                    ) : null}
                  </dd>
                </div>
              </dl>
              {urlMismatch ? (
                <p className="text-sm text-amber-400 mt-4">
                  That URL is a different {channelMeta.name} listing (it does not match this item’s title). Clear it, then List this item — or paste the correct listing link and Save.
                </p>
              ) : null}
              <label className="block text-sm mt-5">
                Listing URL
                <input
                  value={listingUrl}
                  onChange={(e) => {
                    const next = e.target.value;
                    setListingUrl(next);
                    const found = listingUrlInText(selected, next);
                    if (found) persistListed(found, { toast: true, trusted: true, source: "paste" });
                  }}
                  onPaste={(e) => {
                    const found = listingUrlInText(selected, e.clipboardData?.getData("text") || "");
                    if (found) persistListed(found, { toast: true, trusted: true, source: "paste" });
                  }}
                  placeholder={listingUrlExample(selected)}
                  className={`${inp} mt-1 font-mono`}
                />
              </label>
              <div className="flex flex-wrap gap-2 mt-2">
                {listingUrl ? (
                  <button type="button" disabled={busy} onClick={clearListingUrl} className="text-xs text-aura-cyan">
                    Clear listing URL
                  </button>
                ) : null}
              </div>
              {chRow.listing_image ? (
                <div className="mt-4 flex items-center gap-3">
                  <img src={chRow.listing_image} alt="" className="w-16 h-20 object-cover rounded-lg bg-aura-sand" />
                  <p className="text-xs text-aura-muted">Photo from the live {channelMeta.name} listing.</p>
                </div>
              ) : null}
              <p className="text-xs text-aura-muted mt-2">
Stay on this page. Listing and delisting do not add a tab to this window. The listing URL shows up here when listing finishes, and this page says Delisted when delisting finishes. You can also paste {listingUrlExample(selected)}
              </p>
            </div>
          ) : null}

          {selected === "flipkart" ? (
            <div className="mb-8 rounded-2xl border border-aura-border bg-aura-surface p-5" data-testid="flipkart-helper-panel">
              <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Listing helper</div>
              <div className="grid sm:grid-cols-2 gap-6 text-sm">
                <div>
                  <div className="text-[11px] uppercase text-aura-muted">Extension version</div>
                  <div className="mt-1 font-medium">{helperStamp || helperVersion() || "Not on this tab"}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-aura-muted">Seller Hub</div>
                  <div className="mt-1 font-medium">seller.flipkart.com</div>
                </div>
              </div>
              {!helperStamp && !helperVersion() ? (
                <p className="text-sm text-amber-400 mt-4">
                  The Shoptosa helper is not on this tab. Download it, Load unpacked, Reload the helper, then Ctrl+Shift+R this page. The popup must show 1.6.114. Stay signed in to Seller Hub. List in Seller Hub fills Add a Single Listing. Post syncs price and stock after Flipkart assigns the FSN.
                </p>
              ) : (
                <p className="text-sm text-aura-muted mt-4">
                  Helper {helperStamp || helperVersion()} is on this tab. List in Seller Hub fills Add a Single Listing while you stay signed in. Post syncs price and stock when this SKU already has an FSN.
                </p>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <Link to="/seller/extension" className="px-5 py-1.5 text-sm border border-aura-border rounded-full">Download helper</Link>
                <button type="button" disabled={busy} onClick={helperPost} className="px-5 py-1.5 text-sm bg-aura-accent text-white rounded-full">
                  {waitingListed ? "Opening Seller Hub…" : "List in Seller Hub"}
                </button>
              </div>
            </div>
          ) : null}

          {selected !== "shoptosa" && via === "unified" && connected ? (
            <div className="mb-8 rounded-2xl border border-aura-border bg-aura-surface p-5" data-testid="unified-listed-panel">
              <div className="text-center pb-4">
                <div className="text-2xl font-semibold">
                  {status === "sold"
                    ? "This item is marked sold"
                    : status === "delisted"
                      ? "Delisted"
                      : status === "failed"
                        ? `${channelMeta.name} rejected this listing`
                      : status === "listed"
                        ? "Your item is listed"
                        : hasListingLink
                          ? `Saved on ${channelMeta.name}`
                          : `Post this item on ${channelMeta.name}`}
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {showUnifiedPost || status === "listed" || status === "failed" || status === "delisted" ? (
                  <button type="button" disabled={busy} onClick={unifiedPost} data-testid="unified-post-top" className="px-5 py-1.5 text-sm bg-aura-accent text-white rounded-full">
                    {status === "listed" ? `Update on ${channelMeta.name}` : status === "failed" ? `Retry on ${channelMeta.name}` : `Post to ${channelMeta.name}`}
                  </button>
                ) : null}
                {showUnifiedDelist ? (
                  <button type="button" disabled={busy} onClick={unifiedDelist} className="px-5 py-1.5 text-sm border border-aura-border rounded-full text-aura-muted">
                    Delist on {channelMeta.name}
                  </button>
                ) : null}
                {hasListingLink ? (
                  <button type="button" onClick={viewListing} className="px-5 py-1.5 text-sm border border-aura-cyan text-aura-cyan rounded-full">
                    View listing
                  </button>
                ) : null}
                <button type="button" disabled={busy} onClick={markSold} className="px-5 py-1.5 text-sm bg-amber-400 text-black rounded-full">
                  Mark as Sold
                </button>
              </div>
              <div className="mt-5 text-sm">
                <div className="text-[11px] uppercase text-aura-muted">Listing URL</div>
                {listingUrl && /^https?:\/\//i.test(listingUrl) ? (
                  <div className="mt-1 text-aura-cyan break-all select-all">{listingUrl}</div>
                ) : (
                  <div className="mt-1 text-aura-muted">The listing link appears here after Post.</div>
                )}
                {selected === "etsy" && hasListingLink && status !== "listed" ? (
                  <p className="text-sm text-aura-muted mt-3">
                    Etsy drafts and inactive listings open as item not available. The listing is still in your shop. Delist removes it. The public page works after Etsy marks the listing active.
                  </p>
                ) : null}
                {chRow.note ? <p className="text-sm text-aura-muted mt-3">{chRow.note}</p> : null}
              </div>
            </div>
          ) : null}

          <h3 className="text-lg font-medium mb-3">Item Photos</h3>
          {selected !== "shoptosa" ? (
            <p className="text-xs text-aura-muted mb-3">
              Photo changes on this form apply only to {channelMeta.name}. To change photos on every shop, use Step 1 Shoptosa Form. {template.note}
            </p>
          ) : (
            <p className="text-xs text-aura-muted mb-3">{template.note}</p>
          )}
          <div className="flex flex-wrap gap-3 mb-4">
            {form.images.map((src, i) => (
              <div key={`${src}-${i}`} className="relative w-24 h-28 rounded-lg overflow-hidden bg-aura-sand">
                <img src={imgUrl(src)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="w-full py-8 border border-dashed border-aura-border rounded-2xl text-sm text-aura-muted"
          >
            {uploading ? "Uploading…" : "Upload Photo(s)"}
          </button>
          {template.photoNote ? <p className="text-xs text-aura-muted mt-2">({template.photoNote})</p> : null}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(e.target.files)} />

          {orderedGroups.map((g) => {
            const keys = groupedFields[g];
            const fullKeys = keys.filter((k) => fieldMeta(selected, k).full || fieldMeta(selected, k).kind === "textarea");
            const gridKeys = keys.filter((k) => !(fieldMeta(selected, k).full || fieldMeta(selected, k).kind === "textarea"));
            const singleColumn = selected === "mercari";
            return (
              <div key={g} className="grid md:grid-cols-[180px_minmax(0,1fr)] gap-6 mt-10">
                <h3 className="text-lg font-medium">{g}</h3>
                <div className="space-y-4">
                  {singleColumn ? keys.map((key) => renderField(key)) : (
                    <>
                      {fullKeys.map((key) => renderField(key))}
                      {gridKeys.length ? (
                        <div className="grid sm:grid-cols-2 gap-3">
                          {gridKeys.map((key) => renderField(key))}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            );
          })}

          <div className="flex flex-wrap items-center justify-between gap-3 mt-10 pt-6 border-t border-aura-border">
            <button type="button" onClick={resetForm} className="text-sm text-aura-cyan">
              Reset to Shoptosa form
            </button>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => markListed(status === "listed" ? "off" : "listed")}
                className="px-4 py-2 text-sm border border-aura-border rounded-lg"
              >
                {status === "listed" ? "Mark not listed" : "Mark listed"}
              </button>
              {selected !== "shoptosa" && HELPER_BROWSER.has(selected) && (
                listingUrl && (/\/listing\//i.test(listingUrl) || listingUrlInText(selected, listingUrl)) && looksListed ? (
                  <button type="button" onClick={viewListing} className="px-4 py-2 text-sm bg-aura-accent text-white rounded-lg">
                    View Listing
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={helperPost}
                    className="px-4 py-2 text-sm bg-aura-accent text-white rounded-lg"
                  >
                    {waitingListed ? "Listing…" : `List on ${channelMeta.name}`}
                  </button>
                )
              )}
              {selected !== "shoptosa" && via === "unified" && connected && (
                <>
                  {connected ? (
                    <button type="button" disabled={busy} onClick={unifiedPost} data-testid="unified-post-bottom" className="px-4 py-2 text-sm border border-aura-border rounded-lg">
                      {status === "listed" ? `Update on ${channelMeta.name}` : `Post to ${channelMeta.name}`}
                    </button>
                  ) : null}
                  {hasListingLink ? (
                    <button type="button" onClick={viewListing} className="px-4 py-2 text-sm bg-aura-accent text-white rounded-lg">
                      View listing
                    </button>
                  ) : null}
                  {showUnifiedDelist ? (
                    <button type="button" disabled={busy} onClick={unifiedDelist} className="px-4 py-2 text-sm border border-aura-border rounded-lg">
                      Delist on {channelMeta.name}
                    </button>
                  ) : null}
                </>
              )}
              {selected !== "shoptosa" && !connected && (
                <button
                  type="button"
                  onClick={() => beginConnect(connById[selected] || { id: selected, name: channelMeta.name, connect_via: HELPER_BROWSER.has(selected) ? "helper" : "unified" }, navigate)}
                  className="px-4 py-2 text-sm border border-aura-border rounded-lg"
                >
                  Connect {channelMeta.name}
                </button>
              )}
              <button type="button" disabled={busy} onClick={save} className="px-5 py-2.5 bg-aura-accent text-white text-sm rounded-lg inline-flex items-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save {channelMeta.name} form
              </button>
            </div>
          </div>
          </>
          )}
        </section>
      </div>
    </div>
  );
}
